import Color from 'color';
import React from 'react';
import classNames from 'classnames';

import styles from './styles.less';
import { LabelSingleSummary, PrimaryInteractions } from './label-single-summary.js';
import { getIconClass, Icon as IconRaw, PointIcon, isDark } from '../icon/icon.js';


const Icon = ({iconClass}) =>
    <IconRaw iconClass={iconClass} cssClass={styles['label-title-icon-encoded']} />;

function preventPropagation(f) {
    return function(e) {
        e.stopPropagation();
        return f(e);
    };
}

function stopPropagation(e) {
    e.stopPropagation();
}

function stopPropagationIfAnchor(e) {
    const { target } = e;
    if (target && target.tagName && target.tagName.toLowerCase() === 'a') {
        e.stopPropagation();
    }
}

const events = ['onLabelSelected', 'onLabelMouseMove'];

export class Label extends React.Component {
    constructor(props, context) {
        super(props, context);
        events.forEach(eventName => {
            this[eventName] = event => {
                const { props = {} } = this;
                const { [eventName]: dispatch } = props;
                if (!dispatch) {
                    return;
                }
                const {
                    simulating,
                    type,
                    index,
                    renderState,
                    pinned,
                    showFull,
                    sceneSelectionType,
                    hasHighlightedLabel,
                    renderingScheduler
                } = props;
                const { camera } = renderState;
                dispatch({
                    event,
                    simulating,
                    hasHighlightedLabel,
                    isOpen: showFull,
                    labelIndex: index,
                    isSelected: pinned,
                    isLabelEvent: true,
                    componentType: type,
                    renderState,
                    renderingScheduler,
                    camera: renderState.camera,
                    selectionType: sceneSelectionType
                });
            };
        });
        this.onLabelSelected = preventPropagation(this.onLabelSelected);
    }
    componentWillUnmount() {
        events.forEach(eventName => (this[eventName] = undefined));
    }
    render() {
        let {
            showFull,
            propertiesEnabled,
            showActions,
            poiLabelEnabled,
            pinned,
            color,
            strokeWidth,
            background,
            onFilter,
            onExclude,
            encodings,
            tooltipOffsetY = 0,
            sizes,
            pointColors,
            edgeColors,
            scalingFactor,
            pixelRatio,
            type,
            index,
            title,
            columns = [],
            hasSelectedLabel,
            isSelectedLabel,
            isHighlightedLabel,
            importantColumns = [],
            shortenLabels = true,
            onCreateSets,
            ...props
        } = this.props;

        if (title == null || title == '') {
            if (!showFull && !pinned) {
                return null;
            }
        }

        const iconSize =
            type === 'edge'
                ? 30
                : Math.max(5, Math.min(scalingFactor * sizes[index], 80)) / pixelRatio;

        background = showFull || pinned ? new Color(background).alpha(1).toString() : background;

        const noData = Boolean(!columns || !columns.length);
        const arrowStyle = { 
            color: background,
            //from manually inspecting a live tooltip
            position: 'absolute',
            left: `-${8/2}px`,
            marginLeft: 'auto',
            marginRight: 'auto',
            width: '100%', //`${Math.round(((title||'').length + 3)* 1.25)}px`,
            top: '-4px'
        };
        const contentStyle = { color, background, maxWidth: `none` };
        const iconClass = getIconClass({ encodings, type, columns });
        const maybeIconEncoding = ((encodings || {})[type] || {}).icon; 
        const iconShape =  (maybeIconEncoding || {}).shape;
        const asText = (maybeIconEncoding || {}).asText;

        const pointAlpha = (!hasSelectedLabel || iconClass || isSelectedLabel || isHighlightedLabel) ? 1 : 0;
        const pointRgb = {
            r: pointColors[index * 4 + 0] || 0,
            g: pointColors[index * 4 + 1] || 0,
            b: pointColors[index * 4 + 2] || 0
        };

        return (
            <div
                style={props.style}
                className={classNames({
                    [styles[type]]: true,
                    [styles['label']]: true,
                    [styles['has-icon']]: iconClass !== undefined,
                    [styles['on']]: showFull,
                    [styles['properties-enabled']]: propertiesEnabled,
                    [styles['poi-label-enabled']]: poiLabelEnabled,
                    [styles['clicked']]: pinned,
                    [styles['shorten-labels']]: shortenLabels // always unshortened when on|clicked
                })}>
                <PointIcon
                    type={type}
                    iconSize={iconSize}
                    iconClass={iconClass}
                    asText={asText}
                    shape={iconShape}
                    pointRgb={pointRgb}
                    pointAlpha={pointAlpha}
                    strokeWidth={strokeWidth}
                    renderPoint={isSelectedLabel || isHighlightedLabel}
                    columns={columns}
                    badgeEncodings={
                        Object.fromEntries(
                            Object.entries((encodings||{})[type] || {})
                                .filter(([name, val]) => name.startsWith("badge") && val)) } />
                <div
                    style={{
                        left: `-50%`,
                        position: `relative`,
                        marginTop: tooltipOffsetY + 1
                    }}
                    onMouseMove={this.onLabelMouseMove}
                    onMouseDown={(!pinned && this.onLabelSelected) || undefined}
                    onTouchStart={(!pinned && this.onLabelSelected) || undefined}
                    className={classNames({
                        in: true,
                        bottom: true,
                        'bs-tooltip-bottom': true,
                        tooltip: true,
                        show: true,
                        [styles['label-tooltip']]: true
                    })}>
                    <div style={arrowStyle} className={`fa fa-caret-up ${styles['label-arrow']}`} />
                    <div style={contentStyle} className="tooltip-inner">
                        <LabelTitle
                            type={type}
                            index={index}
                            edgeColors={edgeColors}
                            background={background}
                            color={color}
                            encodings={encodings}
                            title={title}
                            iconClass={iconClass}
                            pinned={pinned}
                            properties={columns}
                            showFull={showFull}
                            onExclude={onExclude}
                            onMouseDown={this.onLabelSelected}
                            onTouchStart={this.onLabelSelected}
                        />
                        {(!noData && propertiesEnabled && showFull && (
                                <div className={styles['label-contents']}>
                                    { pinned && showActions && (
                                        <PrimaryInteractions
                                            onExclude={onExclude}
                                            onFilter={onFilter}
                                            onCreateSets={onCreateSets}
                                            toClipboard={title}
                                            interactionDetails={{
                                                value: title,
                                                name: '_title',
                                                dataType: 'equals',
                                                componentType: type
                                            }} />
                                    )}
                                    <LabelSingleSummary
                                        rgb={pointRgb}
                                        iconClass={iconClass}
                                        selection={{}}
                                        onFilter={onFilter}
                                        onExclude={onExclude}
                                        onCreateSets={onCreateSets}
                                        title={title}
                                        type={type}
                                        importantColumns={importantColumns}
                                        columns={columns}
                                        showActions={showActions}
                                        onExpanderChange={() => console.error("UNIMPLEMENTED")}
                                    />
                                </div>
                            )) ||
                            undefined}
                    </div>
                </div>
            </div>
        );
    }
}

const nbsphtml = { __html: '&nbsp;' };
const fldToElt = (prop, properties = []) => {
    const vs = properties.filter(v => v.key === prop);
    return (!vs.length || vs[0].value == null)
        ? <span dangerouslySetInnerHTML={nbsphtml}/>
        : <span dangerouslySetInnerHTML={{ __html: vs[0].value }} />;
};

const arrToColor = ([a,b,c]) => (new Color({r: a || 0, g: b || 0, b: c || 0}).alpha(1));

function LabelTitle({
    type,
    background,
    index,
    edgeColors,
    encodings,
    iconClass,
    title,
    pinned,
    properties,
    showFull,
    onMouseDown,
    onTouchStart
}) {
    const titleHTML = { __html: title };
    const titleExcludeHTML = { __html: title };

    if (title == null || title === '') {
        title = '';
        titleHTML.__html = '&nbsp;';
        titleExcludeHTML.__html = `''`;
    }

    if (!showFull) {
        return (
            <div
                className={styles['label-title']}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}>
                <span onMouseDown={stopPropagationIfAnchor} className={styles['label-title-text']}>
                    <Icon iconClass={iconClass} />
                    <span dangerouslySetInnerHTML={titleHTML} />
                </span>
            </div>
        );
    }

    if (type === 'edge') {

        const isPointColored = !encodings || !encodings['edge'] || !encodings['edge'].color;
        let srcStyle, edgeStyle, dstStyle;
        if (isPointColored) {
            const srcColor = arrToColor(edgeColors.subarray(2 * index * 4,         2 * index * 4         + 3));
            srcStyle = {
                class: styles[(isDark(srcColor.rgb().object()) ? 'dark' : 'light')+'-color'],
                style: {backgroundColor: srcColor.toString()}
            };

            const dstColor = arrToColor(edgeColors.subarray(2 * index * 4 + 1 * 4, 2 * index * 4 + 1 * 4 + 3));
            dstStyle = {
                class: styles[(isDark(dstColor.rgb().object()) ? 'dark' : 'light')+'-color'],
                style: {backgroundColor: dstColor.toString()}
            };

            edgeStyle = {
                class: '',
                style: {}
            }

        } else {
            srcStyle = { style: { background } };
            dstStyle = { style: { background } };

            const srcColor = arrToColor(edgeColors.subarray(2 * index * 4,         2 * index * 4         + 3));
            edgeStyle = {
                class: styles[(isDark(srcColor.rgb().object()) ? 'dark' : 'light')+'-color'],
                style: {backgroundColor: srcColor.toString()}
            };
        }

        return (<div
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            className={`${styles['label-title']} ${styles['edge-title']}`}>
            {/*<a
                className={classNames({
                    [styles['pinned']]: pinned,
                    [styles['label-title-close']]: true
                })}>
                <i
                    style={{ color }}
                    className={classNames({
                        fa: true,
                        'fa-times': true
                    })}
                />
            </a>*/}
            <span className={`${styles['source']} ${ srcStyle.class }`} style={srcStyle.style}>{
                fldToElt('source', properties)
            }</span>
            <span onMouseDown={stopPropagationIfAnchor} className={`${styles['label-title-text']} ${edgeStyle.class}`} style={edgeStyle.style}>
                <Icon iconClass={iconClass} />
                <span
                    dangerouslySetInnerHTML={titleHTML}
                    style={(pinned && { display: 'inline-block' }) || undefined}
                />
            </span>
            <span className={`${styles['destination']} ${ dstStyle.class }`} style={dstStyle.style}>{
                fldToElt('destination', properties)
            }</span>
        </div>);
    }

    return (
        <div
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            className={styles['label-title']}>
            {/*<a
                className={classNames({
                    [styles['pinned']]: pinned,
                    [styles['label-title-close']]: true
                })}>
                <i
                    style={{ color }}
                    className={classNames({
                        fa: true,
                        'fa-times': true
                    })}
                />
            </a>*/}
            <span onMouseDown={stopPropagationIfAnchor} className={styles['label-title-text']}>
                <Icon iconClass={iconClass} />
                <span
                    dangerouslySetInnerHTML={titleHTML}
                    style={(pinned && { display: 'inline' }) || undefined}
                />
            </span>
        </div>
    );
}
